import React from 'react';
import { CalendarIcon as DateInputCalendarIcon } from '../../../../ui/Icons';
import './CustomFieldEditor.css';

/**
 * CustomFieldEditor Component
 * Editor UI for setting custom field values
 */
const CustomFieldEditor = ({
  editingCustomField,
  customFieldInputValue,
  setCustomFieldInputValue,
  customFieldInputRef,
  customFields,
  onBack,
  onApply,
}) => {
  const currentField = customFields.find(f => f.id === editingCustomField.fieldId);

  const handleKeyDown = (e, getValue) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      onApply(editingCustomField.fieldId, getValue());
    } else if (e.key === 'Escape') {
      onBack();
    }
  };

  return (
    <div className="bulk-actions-toolbar__custom-field-editor">
      <div className="bulk-actions-toolbar__custom-field-editor-header">
        <button 
          className="bulk-actions-toolbar__custom-field-back"
          onClick={onBack}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <span className="bulk-actions-toolbar__custom-field-editor-title">
          {currentField?.name}
        </span>
      </div>
      
      {/* Text input */}
      {editingCustomField.fieldType === 'text' && (
        <div className="bulk-actions-toolbar__custom-field-input-row">
          <input
            ref={customFieldInputRef}
            type="text"
            className="bulk-actions-toolbar__custom-field-input"
            placeholder="Enter text..."
            value={customFieldInputValue}
            onChange={(e) => setCustomFieldInputValue(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, () => customFieldInputValue)}
          />
          <button 
            className="bulk-actions-toolbar__custom-field-apply"
            onClick={() => onApply(editingCustomField.fieldId, customFieldInputValue)}
          >
            Apply
          </button>
        </div>
      )}
      
      {/* Number input */}
      {editingCustomField.fieldType === 'number' && (
        <div className="bulk-actions-toolbar__custom-field-input-row">
          <input
            ref={customFieldInputRef}
            type="number"
            className="bulk-actions-toolbar__custom-field-input"
            placeholder="Enter number..."
            value={customFieldInputValue}
            onChange={(e) => setCustomFieldInputValue(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, () => e.target.value === '' ? null : Number(e.target.value))}
          />
          <button 
            className="bulk-actions-toolbar__custom-field-apply"
            onClick={() => {
              const numValue = customFieldInputValue === '' ? null : Number(customFieldInputValue);
              onApply(editingCustomField.fieldId, numValue);
            }}
          >
            Apply
          </button>
        </div>
      )}
      
      {/* URL input */}
      {editingCustomField.fieldType === 'url' && (
        <div className="bulk-actions-toolbar__custom-field-input-row">
          <input
            ref={customFieldInputRef}
            type="url"
            className="bulk-actions-toolbar__custom-field-input"
            placeholder="https://..."
            value={customFieldInputValue}
            onChange={(e) => setCustomFieldInputValue(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, () => customFieldInputValue || '')}
          />
          <button 
            className="bulk-actions-toolbar__custom-field-apply"
            onClick={() => onApply(editingCustomField.fieldId, customFieldInputValue || '')}
          >
            Apply
          </button>
        </div>
      )}
      
      {/* Date input */}
      {editingCustomField.fieldType === 'date' && (
        <>
          <div className="bulk-actions-toolbar__date-presets">
            <button 
              className="bulk-actions-toolbar__menu-item" 
              onClick={() => onApply(editingCustomField.fieldId, null)}
            >
              Clear date
            </button>
            <button 
              className="bulk-actions-toolbar__menu-item" 
              onClick={() => onApply(editingCustomField.fieldId, new Date().toISOString().split('T')[0])}
            >
              Today
            </button>
            <button 
              className="bulk-actions-toolbar__menu-item" 
              onClick={() => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                onApply(editingCustomField.fieldId, tomorrow.toISOString().split('T')[0]);
              }}
            >
              Tomorrow
            </button>
          </div>
          <div className="bulk-actions-toolbar__date-input-row">
            <input
              ref={customFieldInputRef}
              type="date"
              className="bulk-actions-toolbar__date-input"
              value={customFieldInputValue}
              onChange={(e) => onApply(editingCustomField.fieldId, e.target.value || null)}
            />
            <span 
              className="bulk-actions-toolbar__date-input-icon-wrapper"
              onClick={(e) => {
                e.stopPropagation();
                e.currentTarget.previousElementSibling?.showPicker?.();
              }}
            >
              <DateInputCalendarIcon size={14} />
            </span>
          </div>
        </>
      )}
      
      {/* Checkbox options */}
      {editingCustomField.fieldType === 'checkbox' && (
        <>
          <button 
            className="bulk-actions-toolbar__menu-item" 
            onClick={() => onApply(editingCustomField.fieldId, true)}
          >
            <span className="bulk-actions-toolbar__checkbox-icon bulk-actions-toolbar__checkbox-icon--checked">✓</span>
            <span>Checked</span>
          </button>
          <button 
            className="bulk-actions-toolbar__menu-item" 
            onClick={() => onApply(editingCustomField.fieldId, false)}
          >
            <span className="bulk-actions-toolbar__checkbox-icon">○</span>
            <span>Unchecked</span>
          </button>
        </>
      )}
      
      {/* Select/Dropdown options */}
      {editingCustomField.fieldType === 'select' && (
        <>
          <button 
            className="bulk-actions-toolbar__menu-item" 
            onClick={() => onApply(editingCustomField.fieldId, null)}
          >
            <span className="bulk-actions-toolbar__select-clear">—</span>
            <span>Clear selection</span>
          </button>
          {(currentField?.options || []).map(option => (
            <button
              key={option.id}
              className="bulk-actions-toolbar__menu-item"
              onClick={() => onApply(editingCustomField.fieldId, option.id)}
            >
              <span 
                className="bulk-actions-toolbar__select-dot" 
                style={{ background: option.color || '#6b7280' }} 
              />
              <span>{option.label}</span>
            </button>
          ))}
        </>
      )}
    </div>
  );
};

export default CustomFieldEditor;
